import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useWindowSize } from 'usehooks-ts';
import { BasketProductItem } from 'pages/Totem/Aside/Basket/BasketProductItem';
import { FadeIn } from 'components/FadeIn';
import { RequestedOrderProductFragmentFragment } from 'data/__generated__';

interface OrderDetailsProductsProps {
    products: RequestedOrderProductFragmentFragment[];
    readOnly?: boolean;
}

export const OrderDetailsProducts: React.FC<OrderDetailsProductsProps> = ({ products, readOnly }) => {
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [numberOfColumns, setNumberOfColumns] = useState<number>(3);
    const [productsPerColumn, setProductsPerColumn] = useState<number>(5);

    const { width, height } = useWindowSize();

    const productsPerPage = productsPerColumn * numberOfColumns;
    const numberOfPages = Math.ceil(products.length / productsPerPage);

    useEffect(() => {
        if (currentPage > numberOfPages) {
            setCurrentPage(0);
        }
        if (height > 1300) {
            setProductsPerColumn(8);
        } else if (height > 1150) {
            setProductsPerColumn(7);
        } else if (height > 1050) {
            setProductsPerColumn(6);
        } else if (height > 950) {
            setProductsPerColumn(5);
        } else if (height > 850) {
            setProductsPerColumn(4);
        } else {
            setProductsPerColumn(3);
        }
    }, [height, currentPage, numberOfPages]);

    useEffect(() => {
        if (currentPage > numberOfPages) {
            setCurrentPage(0);
        }
        if (width > 1400) {
            setNumberOfColumns(3);
        } else if (width > 1000) {
            setNumberOfColumns(2);
        } else {
            setNumberOfColumns(1);
        }
    }, [width, currentPage, numberOfPages]);

    return (
        <>
            <OrderDetailsProductsContainer>
                {Array.from({ length: numberOfColumns }).map((_, index) => {
                    const columnProducts = products.slice(
                        currentPage * productsPerColumn * numberOfColumns + productsPerColumn * index,
                        currentPage * productsPerColumn * numberOfColumns + productsPerColumn * (index + 1),
                    );
                    if (columnProducts.length > 0) {
                        return (
                            <OrderDetailsProductsColumn key={index} hasRightBorder={index !== numberOfColumns - 1}>
                                {columnProducts.map((product, idx) => (
                                    <FadeIn key={product._id} duration={500} delay={idx * 10}>
                                        <BasketProductItem product={product} readOnly={readOnly} />
                                    </FadeIn>
                                ))}
                            </OrderDetailsProductsColumn>
                        );
                    }
                    return null;
                })}
            </OrderDetailsProductsContainer>
            {products.length > productsPerColumn * numberOfColumns && (
                <PageCounter>
                    <strong>Page</strong>
                    {Array.from({ length: numberOfPages }).map((_, index) => (
                        <React.Fragment key={index}>
                            <span>{index !== 0 && '/'}</span>
                            <PageNumber onClick={() => setCurrentPage(index)} isActive={index === currentPage}>
                                {index + 1}
                            </PageNumber>
                        </React.Fragment>
                    ))}
                </PageCounter>
            )}
        </>
    );
};

type OrderDetailsProductProps = {
    isActive?: boolean;
    hasRightBorder?: boolean;
};
const PageNumber = styled.span<OrderDetailsProductProps>`
    font-weight: ${({ isActive }) => isActive && 'bold'};
    padding: 0 5px;
    cursor: pointer;
`;

const OrderDetailsProductsColumn = styled.div<OrderDetailsProductProps>`
    display: flex;
    flex-direction: column;
    border-right: ${({ theme, hasRightBorder }) => hasRightBorder && `1px solid ${theme.colors.borderGrey}`};
    margin: 30px 0;
`;

const OrderDetailsProductsContainer = styled.div`
    padding: 0 35px 0 20px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
    @media only screen and (max-width: 1400px) {
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    }
    @media only screen and (max-width: 1000px) {
        grid-template-columns: minmax(0, 1fr);
    }
    ${OrderDetailsProductsColumn}:not(:first-child) {
        padding-left: 30px;
    }
    ${OrderDetailsProductsColumn}:not(:last-child) {
        padding-right: 30px;
    }
`;

const PageCounter = styled.div`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    margin: 0 20px 20px 20px;
`;
